/**
 * Custom CKEditor Plugin for Font Background Color Editing
 * 
 * This plugin enables the handling of custom background colors for text in CKEditor.
 * It defines the required schema, converters, and commands to support applying, 
 * removing, and rendering background color attributes on text nodes.
 * 
 * ## Key Features
 * - Supports applying background colors to text using a custom attribute (`data-bg-color`).
 * - Converts legacy `<font>` elements with `color` attributes for backward compatibility.
 * - Handles both upcasting (HTML to model) and downcasting (model to HTML).
 * - Extends the schema to allow the `data-bg-color` attribute on text nodes.
 * - Defines a high-priority attribute element for consistent downcasting.
 * 
 * ### Core Components
 * - **Upcasting**: Maps `data-bg-color` attributes from HTML to the editor model.
 * - **Downcasting**: Converts the model attribute back to a `span` element with the `data-bg-color` attribute.
 * - **Commands**: Registers the `CustomFontBackgroundColorCommand` for applying and managing the background color attribute.
 * 
 * ### Schema Properties
 * - `isFormatting`: Marks the attribute as formatting to enable consistent behavior during editing.
 * - `copyOnEnter`: Ensures the attribute is preserved when splitting lines.
 * 
 * ### References
 * - CKEditor Plugin API: 
 *   https://ckeditor.com/docs/ckeditor5/latest/framework/guides/creating-simple-plugin.html
 * - CKEditor Conversion API:
 *   https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_conversion_conversion-Conversion.html
 * 
 * @module CustomFontBackgroundColorEditing
 * @exports CustomFontBackgroundColorEditing
 */

import { CKPlugins } from '../../constants/constants';
import { CustomFontBackgroundColorCommand } from './fontBackgroundColorCommand';
import { CustomBackgroundFontColorUI } from './fontBackgroundColorUI';

const { Plugin } = window.ClassicEditor;

export class CustomFontBackgroundColorEditing extends Plugin {
    static get pluginName() {
        return 'CustomFontBackgroundColorEditing';
    }

    static get requires() {
        return [ CustomFontBackgroundColorEditing, CustomBackgroundFontColorUI ];
    }

    constructor(editor) {
        super(editor);

        editor.conversion.for('upcast').elementToAttribute({
            view: {
                name: 'span',
                attributes: {
                    'data-bg-color': true,
                }
            },
            model: {
                key: CKPlugins.CustomFontBackgroundColor,
                value: viewElement => viewElement.getAttribute('data-bg-color')
            }
        });

        // Support legacy `<font color="..">` formatting.
        editor.conversion.for('upcast').elementToAttribute({
            view: {
                name: 'font',
                attributes: {
                    'data-bg-color': /^#?\w+$/
                }
            },
            model: {
                key: CKPlugins.CustomFontBackgroundColor,
                value: viewElement => viewElement.getAttribute('data-bg-color')
            }
        });

        editor.conversion.for('downcast').attributeToElement( {
            model: CKPlugins.CustomFontBackgroundColor,
            view: this.renderDowncastElement()
        } );

        editor.commands.add(CKPlugins.CustomFontBackgroundColor, new CustomFontBackgroundColorCommand(editor));

        // Allow the font color attribute on text nodes.
        editor.model.schema.extend('$text', { allowAttributes: [CKPlugins.CustomFontBackgroundColor] });

        editor.model.schema.setAttributeProperties(CKPlugins.CustomFontBackgroundColor, {
            isFormatting: true,
            copyOnEnter: true
        });

    }

    renderDowncastElement() {
        return (modelAttributeValue, { writer } ) => {
            return writer.createAttributeElement('span', {
                'data-bg-color': modelAttributeValue
            }, { priority: 7 });
        };
    }
}
